import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";

export type EventType = Database["public"]["Enums"]["event_type"];
export type EventStatus = Database["public"]["Enums"]["event_status"];
export type EventOutcome = "open" | "won" | "lost";

type BaseEventRow = Database["public"]["Tables"]["events"]["Row"];
export type EventLineItem = Database["public"]["Tables"]["event_line_items"]["Row"];

export type EventRow = BaseEventRow & { outcome: EventOutcome };

export interface EventWithItems extends EventRow {
  items?: EventLineItem[];
}

export const EVENT_STATUSES: EventStatus[] = [
  "new",
  "contacted",
  "quoted",
  "booked_direct",
  "won",
  "lost",
  "confirmed",
  "in_progress",
  "completed",
  "cancelled",
];

// Booked+ statuses: the default view on /admin/events (#1032). The quote pipeline
// writes these through sync_event_status_from_quote(): deposit_pending -> won,
// booked -> confirmed, won -> completed. `won` means the client accepted but the
// deposit isn't in yet; it stays in on purpose, since the dashboard counts
// deposit-pending as sold. booked_direct and in_progress are set by hand.
// Pre-booking (new, contacted, quoted) and lost/cancelled are left out.
export const BOOKED_EVENT_STATUSES: EventStatus[] = [
  "booked_direct",
  "won",
  "confirmed",
  "in_progress",
  "completed",
];

export const EVENT_TYPES: EventType[] = [
  "wedding",
  "live_service",
  "corporate",
  "drop_off",
  "venue",
  "other",
];

export const EVENT_OUTCOMES: EventOutcome[] = ["open", "won", "lost"];

// Valid next-statuses for each current status. Enforced at the UI layer.
// DB trigger records the transition; app-layer guard prevents illegal ones.
export const VALID_TRANSITIONS: Record<EventStatus, EventStatus[]> = {
  new: ["contacted", "lost"],
  contacted: ["quoted", "booked_direct", "lost"],
  quoted: ["won", "lost"],
  booked_direct: ["confirmed", "cancelled"],
  won: ["confirmed"],
  confirmed: ["in_progress", "cancelled"],
  in_progress: ["completed", "cancelled"],
  completed: [],
  lost: ["contacted"],
  cancelled: [],
};

export function canTransition(from: EventStatus, to: EventStatus): boolean {
  return VALID_TRANSITIONS[from]?.includes(to) ?? false;
}

export interface EventsFilters {
  /** "booked" = any of BOOKED_EVENT_STATUSES. */
  status?: EventStatus | "all" | "booked";
  eventType?: EventType | "all";
  outcome?: EventOutcome | "all";
  assignedTo?: string | "all" | "unassigned";
  search?: string;
  /** Include partial (incomplete, #226) leads. Default false — they're kept out
   * of the main list + counts so half-finished wizard forms don't inflate them. */
  includePartial?: boolean;
  /** Exclude past events (event_date < today). Default false. #591 */
  excludePast?: boolean;
}

/** Rows per page on /admin/events (#1165). */
export const EVENTS_PAGE_SIZE = 100;

export interface EventsPage {
  events: EventRow[];
  /** Every row matching the filters, across all pages. */
  totalCount: number;
}

// The events query with EventsFilters applied, counting every matching row.
// `head` asks for the count alone (useEvents' fallback past the last page).
function filteredEventsQuery(filters: EventsFilters, head = false) {
  let q = supabase.from("events").select("*", { count: "exact", head });

  // Hide partial leads unless explicitly requested.
  if (!filters.includePartial) q = q.eq("is_partial", false);

  if (filters.status === "booked") q = q.in("status", BOOKED_EVENT_STATUSES);
  else if (filters.status && filters.status !== "all") q = q.eq("status", filters.status);
  if (filters.eventType && filters.eventType !== "all") q = q.eq("event_type", filters.eventType);
  if (filters.outcome && filters.outcome !== "all") q = q.eq("outcome", filters.outcome);
  if (filters.assignedTo === "unassigned") q = q.is("assigned_to", null);
  else if (filters.assignedTo && filters.assignedTo !== "all") q = q.eq("assigned_to", filters.assignedTo);

  if (filters.search && filters.search.trim().length > 0) {
    const term = `%${filters.search.trim()}%`;
    q = q.or(
      `client_name.ilike.${term},client_email.ilike.${term},event_number.ilike.${term}`
    );
  }

  // Hide past events unless explicitly showing them (#591)
  if (filters.excludePast) {
    const today = new Date().toISOString().split("T")[0];
    // Undated rows count as upcoming, except historical GHL backfills (#1005)
    // and the events backfilled for old quotes (#1427).
    // source is nullable and not.in drops NULLs, hence the source.is.null arm.
    q = q.or(
      `event_date.gte.${today},and(event_date.is.null,source.is.null),and(event_date.is.null,source.not.in.(ghl_backfill,backfill_orphan_quote))`
    );
  }
  return q;
}

/** One page of events matching `filters`, plus the total across all pages (#1165). */
export function useEvents(filters: EventsFilters = {}, page = 0) {
  return useQuery({
    queryKey: ["events", filters, page],
    queryFn: async (): Promise<EventsPage> => {
      const from = page * EVENTS_PAGE_SIZE;
      const { data, error, count } = await filteredEventsQuery(filters)
        .order("created_at", { ascending: false })
        .order("id", { ascending: false }) // tie-break so offsets are stable
        .range(from, from + EVENTS_PAGE_SIZE - 1);

      if (error) {
        // An offset past the last row (rows left the filter since the page was
        // opened) is a 416 with no count. Fetch the count so the page can clamp.
        if (error.code !== "PGRST103") throw error;
        const head = await filteredEventsQuery(filters, true);
        if (head.error) throw head.error;
        return { events: [], totalCount: head.count ?? 0 };
      }

      const events = (data ?? []) as EventRow[];
      return { events, totalCount: count ?? events.length };
    },
    // Keep the current rows on screen while the next page loads, but not
    // across a filter change, where they'd be the wrong rows.
    placeholderData: (prev, prevQuery) =>
      JSON.stringify(prevQuery?.queryKey[1]) === JSON.stringify(filters) ? prev : undefined,
  });
}

/** Count of partial (incomplete, #226) leads — drives the admin "Show incomplete" toggle. */
export function usePartialLeadCount() {
  return useQuery({
    queryKey: ["events-partial-count"],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("events")
        .select("id", { count: "exact", head: true })
        .eq("is_partial", true);
      if (error) throw error;
      return count ?? 0;
    },
  });
}

export function useEventDetail(eventId: string | null) {
  return useQuery({
    queryKey: ["event-detail", eventId],
    queryFn: async () => {
      if (!eventId) return null;
      const { data: event, error } = await supabase
        .from("events")
        .select("*")
        .eq("id", eventId)
        .single();
      if (error) throw error;

      const { data: items, error: itemsErr } = await supabase
        .from("event_line_items")
        .select("*")
        .eq("event_id", eventId)
        .order("sort_order");
      if (itemsErr) throw itemsErr;

      return { ...event, items: items ?? [] } as EventWithItems;
    },
    enabled: !!eventId,
  });
}

export function useUpdateEventStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      status,
      fromStatus,
      note,
    }: {
      id: string;
      status: EventStatus;
      fromStatus: EventStatus;
      note?: string;
    }) => {
      if (!canTransition(fromStatus, status)) {
        throw new Error(`Invalid status transition: ${fromStatus} → ${status}`);
      }

      const { error } = await supabase
        .from("events")
        .update({ status })
        .eq("id", id);
      if (error) throw error;

      // DB trigger inserts a history row automatically. If a note was provided,
      // update the most recent history row for this event with the note.
      if (note && note.trim().length > 0) {
        const { data: latest } = await supabase
          .from("event_status_history")
          .select("id")
          .eq("event_id", id)
          .order("changed_at", { ascending: false })
          .limit(1)
          .maybeSingle();
        if (latest?.id) {
          await supabase
            .from("event_status_history")
            .update({ note })
            .eq("id", latest.id);
        }
      }
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["events"] });
      qc.invalidateQueries({ queryKey: ["event-detail", variables.id] });
      qc.invalidateQueries({ queryKey: ["event-status-history", variables.id] });
    },
  });
}

export function useAssignEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, userId }: { id: string; userId: string | null }) => {
      const { error } = await supabase
        .from("events")
        .update({ assigned_to: userId })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["events"] });
      qc.invalidateQueries({ queryKey: ["event-detail", variables.id] });
    },
  });
}

export function useUpdateEventNotes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      notes,
      internalNotes,
    }: {
      id: string;
      notes?: string;
      internalNotes?: string;
    }) => {
      const patch: { notes?: string; internal_notes?: string } = {};
      if (notes !== undefined) patch.notes = notes;
      if (internalNotes !== undefined) patch.internal_notes = internalNotes;
      if (Object.keys(patch).length === 0) return;

      const { error } = await supabase.from("events").update(patch).eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["events"] });
      qc.invalidateQueries({ queryKey: ["event-detail", variables.id] });
    },
  });
}

export function useUpdateEventOutcome() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, outcome }: { id: string; outcome: EventOutcome }) => {
      // outcome column added in migration; types regenerated on next sync
      const { error } = await supabase
        .from("events")
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .update({ outcome } as any)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["events"] });
      qc.invalidateQueries({ queryKey: ["event-detail", variables.id] });
    },
  });
}

// Human-readable labels
export const EVENT_TYPE_LABELS: Record<EventType, string> = {
  wedding: "Wedding",
  live_service: "Live Service",
  corporate: "Corporate",
  drop_off: "Drop-off / Pickup",
  venue: "Venue",
  other: "Other",
};

export const EVENT_STATUS_LABELS: Record<EventStatus, string> = {
  new: "New",
  contacted: "Contacted",
  quoted: "Quoted",
  booked_direct: "Booked (Direct)",
  won: "Won",
  lost: "Lost",
  confirmed: "Confirmed",
  in_progress: "In Progress",
  completed: "Completed",
  cancelled: "Cancelled",
};

// Tailwind color classes by status for badges
export const EVENT_STATUS_BADGE: Record<EventStatus, string> = {
  new: "bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30",
  contacted: "bg-violet-500/15 text-violet-700 dark:text-violet-300 border-violet-500/30",
  quoted: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30",
  booked_direct: "bg-cyan-500/15 text-cyan-700 dark:text-cyan-300 border-cyan-500/30",
  won: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30",
  lost: "bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30",
  confirmed: "bg-teal-500/15 text-teal-700 dark:text-teal-300 border-teal-500/30",
  in_progress: "bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 border-indigo-500/30",
  completed: "bg-emerald-600/15 text-emerald-800 dark:text-emerald-200 border-emerald-600/30",
  cancelled: "bg-zinc-500/15 text-zinc-700 dark:text-zinc-300 border-zinc-500/30",
};

export const EVENT_OUTCOME_LABELS: Record<EventOutcome, string> = {
  open: "Open",
  won: "Won",
  lost: "Lost",
};

export const EVENT_OUTCOME_BADGE: Record<EventOutcome, string> = {
  open: "bg-slate-500/15 text-slate-700 dark:text-slate-300 border-slate-500/30",
  won: "bg-green-500/15 text-green-700 dark:text-green-300 border-green-500/30",
  lost: "bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/30",
};
